/**
 * @description Customer-portal status pills. Invoice overdue prefers API displayStatus.
 * @dependencies dateUtils.parseDateSafely, isPortalInvoiceOverdue
 * @notes displayStatus is computed with company-local SQL. Do not re-derive
 * overdue from the customer's laptop timezone.
 */
import React from 'react';
import { parseDateSafely, isPortalInvoiceOverdue } from '../../utils/dateUtils';

const StatusBadge = ({ type, value, size = 'sm' }) => {
  const sizeClasses = {
    xs: 'px-2 py-0.5 text-xs',
    sm: 'px-2.5 py-1 text-sm',
    md: 'px-3 py-1.5 text-base'
  };

  const getStyleForType = () => {
    switch (type) {
      case 'invoice_status':
        const { status: invStatus, paymentStatus: invPaymentStatus } = value || {};

        // Priority order for customer portal
        if (invPaymentStatus === 'paid') return 'bg-green-100 text-green-800';
        if (invPaymentStatus === 'partially_paid') return 'bg-blue-100 text-blue-800';
        if (invPaymentStatus === 'voided') return 'bg-gray-100 text-gray-800';
        if (invStatus === 'cancelled') return 'bg-gray-100 text-gray-800';

        if (isPortalInvoiceOverdue(value) && (invPaymentStatus === 'unpaid' || invPaymentStatus === 'partially_paid')) {
          return 'bg-red-100 text-red-800';
        }

        // Default unpaid status
        return 'bg-yellow-100 text-yellow-800';

      case 'estimate_status': {
        const { status: estStatus, expirationDate: estExpDate } = value || {};
        const estToday = new Date();
        estToday.setHours(0, 0, 0, 0);
        const estExp = parseDateSafely(estExpDate);
        const estIsExpired = estStatus === 'sent' && estExp && estExp < estToday;

        if (estIsExpired) return 'bg-orange-100 text-orange-800';
        if (estStatus === 'draft') return 'bg-gray-100 text-gray-800';
        if (estStatus === 'sent') return 'bg-blue-100 text-blue-800';
        if (estStatus === 'accepted') return 'bg-green-100 text-green-800';
        if (estStatus === 'declined') return 'bg-red-100 text-red-800';
        if (estStatus === 'expired') return 'bg-orange-100 text-orange-800';
        if (estStatus === 'converted') return 'bg-purple-100 text-purple-800';
        return 'bg-gray-100 text-gray-800';
      }

      case 'transaction_status':
        const { status: txStatus, isVoided } = value || {};
        if (isVoided) return 'bg-gray-100 text-gray-800';
        if (txStatus === 'refunded' || txStatus === 'partially_refunded') return 'bg-orange-100 text-orange-800';
        if (txStatus === 'APPROVAL' || txStatus === 'completed') return 'bg-green-100 text-green-800';
        if (txStatus === 'DECLINED') return 'bg-red-100 text-red-800';
        if (txStatus === 'PENDING') return 'bg-yellow-100 text-yellow-800';
        return 'bg-gray-100 text-gray-800';

      case 'payment_result':
        switch (value?.toLowerCase()) {
          case 'success':
          case 'completed':
            return 'bg-green-100 text-green-800';
          case 'pending':
          case 'processing':
            return 'bg-blue-100 text-blue-800';
          case 'failed':
          case 'declined':
            return 'bg-red-100 text-red-800';
          default:
            return 'bg-gray-100 text-gray-800';
        }

      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDisplayValue = () => {
    if (!value) return 'N/A';

    switch (type) {
      case 'invoice_status':
        const { status: invStatus, paymentStatus: invPaymentStatus } = value || {};

        // Priority order for customer portal - customer-friendly labels
        if (invPaymentStatus === 'paid') return 'Paid';
        if (invPaymentStatus === 'partially_paid') return 'Partially Paid';
        if (invPaymentStatus === 'voided') return 'Voided';
        if (invStatus === 'cancelled') return 'Cancelled';

        if (isPortalInvoiceOverdue(value) && (invPaymentStatus === 'unpaid' || invPaymentStatus === 'partially_paid')) {
          return 'Overdue';
        }

        // Default unpaid status
        return 'Unpaid';

      case 'estimate_status': {
        const { status: estStatus2, expirationDate: estExpDate2 } = value || {};
        const estToday2 = new Date();
        estToday2.setHours(0, 0, 0, 0);
        const estExp2 = parseDateSafely(estExpDate2);
        const estIsExpired2 = estStatus2 === 'sent' && estExp2 && estExp2 < estToday2;

        if (estIsExpired2) return 'Expired';
        const estLabels = {
          draft: 'Draft', sent: 'Sent', accepted: 'Accepted',
          declined: 'Declined', expired: 'Expired', converted: 'Converted'
        };
        return estLabels[estStatus2] || estStatus2;
      }

      case 'transaction_status':
        const { status: txStatus2, isVoided: isVoided2 } = value || {};
        if (isVoided2) return 'Voided';
        if (txStatus2 === 'refunded') return 'Refunded';
        if (txStatus2 === 'partially_refunded') return 'Partially Refunded';
        if (txStatus2 === 'APPROVAL' || txStatus2 === 'completed') return 'Completed';
        if (txStatus2 === 'DECLINED') return 'Declined';
        if (txStatus2 === 'PENDING') return 'Processing';
        return txStatus2 || 'Unknown';

      case 'payment_result':
        switch (value?.toLowerCase()) {
          case 'success':
          case 'completed':
            return 'Payment Successful';
          case 'pending':
          case 'processing':
            return 'Processing Payment';
          case 'failed':
          case 'declined':
            return 'Payment Failed';
          default:
            return 'Unknown Status';
        }

      default:
        // Format generic values (replace underscores, capitalize)
        return typeof value === 'string' 
          ? value.replace(/_/g, ' ').split(' ').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            ).join(' ')
          : String(value);
    }
  };

  return (
    <span className={`inline-flex items-center font-medium rounded-full ${sizeClasses[size]} ${getStyleForType()}`}>
      {getDisplayValue()}
    </span>
  );
};

export default StatusBadge;